/**
 * Panel administrativo · Gestión de Delegados Regionales.
 *
 * Permite al admin crear/editar/borrar configuraciones de delegados regionales
 * que reciben oportunidades filtradas por zona en /delegado.
 *
 * Incluye:
 *  - Checkboxes de provincias agrupadas por CCAA.
 *  - Slider para score_min (umbral Pando).
 *  - Input high_value_floor (€) — puerta secundaria: opps de importe alto entran aunque score < score_min.
 */
import React, { useEffect, useState, useMemo, useRef } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { API } from "@/context/AuthContext";
import { MapPin, Plus, Edit2, Trash2, Loader2, Save, X, RefreshCw, Mail, Euro, Sliders, Eye, TrendingUp, Map as MapIcon, AlertCircle, Upload, Download } from "lucide-react";
import { toast } from "sonner";

// Áreas Pando por defecto (core de Valoriza)
const DEFAULT_AI_AREAS = [
  "residuos", "recogida", "transporte_residuos", "limpieza_viaria",
  "contenerizacion", "plantas_tratamiento", "zonas_verdes", "movilidad",
];

// Provincias españolas agrupadas por CCAA (para checkboxes)
const CCAA_GROUPS = {
  "Andalucía":            ["Almería", "Cádiz", "Córdoba", "Granada", "Huelva", "Jaén", "Málaga", "Sevilla"],
  "Aragón":               ["Huesca", "Teruel", "Zaragoza"],
  "Asturias":             ["Asturias"],
  "Baleares":             ["Baleares"],
  "Canarias":             ["Las Palmas", "Santa Cruz de Tenerife"],
  "Cantabria":            ["Cantabria"],
  "Castilla-La Mancha":   ["Albacete", "Ciudad Real", "Cuenca", "Guadalajara", "Toledo"],
  "Castilla y León":      ["Ávila", "Burgos", "León", "Palencia", "Salamanca", "Segovia", "Soria", "Valladolid", "Zamora"],
  "Cataluña":             ["Barcelona", "Girona", "Lleida", "Tarragona"],
  "Comunidad Valenciana": ["Alicante", "Castellón", "Valencia"],
  "Extremadura":          ["Badajoz", "Cáceres"],
  "Galicia":              ["A Coruña", "Lugo", "Ourense", "Pontevedra"],
  "La Rioja":             ["La Rioja"],
  "Madrid":               ["Madrid"],
  "Murcia":               ["Murcia"],
  "Navarra":              ["Navarra"],
  "País Vasco":           ["Álava", "Gipuzkoa", "Bizkaia"],
  "Ceuta y Melilla":      ["Ceuta", "Melilla"],
};

// Sinónimos aceptados por CCAA (para checkbox "toda la CCAA")
const CCAA_ALIASES = {
  "Andalucía":            ["Andalucía"],
  "Aragón":               ["Aragón"],
  "Asturias":             ["Principado de Asturias", "Asturias"],
  "Baleares":             ["Islas Baleares", "Illes Balears"],
  "Canarias":             ["Canarias"],
  "Cantabria":            ["Cantabria"],
  "Castilla-La Mancha":   ["Castilla-La Mancha", "Castilla La Mancha"],
  "Castilla y León":      ["Castilla y León"],
  "Cataluña":             ["Cataluña", "Catalunya"],
  "Comunidad Valenciana": ["Comunidad Valenciana", "Comunitat Valenciana"],
  "Extremadura":          ["Extremadura"],
  "Galicia":              ["Galicia"],
  "La Rioja":             ["La Rioja"],
  "Madrid":               ["Comunidad de Madrid", "Madrid"],
  "Murcia":               ["Región de Murcia", "Murcia"],
  "Navarra":              ["Navarra", "Comunidad Foral de Navarra"],
  "País Vasco":           ["País Vasco", "Euskadi"],
  "Ceuta y Melilla":      ["Ceuta", "Melilla"],
};

const EMPTY_FORM = {
  email: "", nombre: "", provincias: [], comunidades: [],
  ai_areas: DEFAULT_AI_AREAS, score_min: 75, high_value_floor: 200000, activo: true,
};

const fmtEUR = (n) => new Intl.NumberFormat("es-ES", { maximumFractionDigits: 0 }).format(n || 0);

export default function DelegadosPanel() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [items, setItems]     = useState([]);
  const [editing, setEditing] = useState(null);
  const [showMap, setShowMap] = useState(true);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [pendingFile, setPendingFile] = useState(null);
  const fileInputRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/delegado/admin/delegados`);
      setItems(r.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("common.error"));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // Iter 130 · Refresh automático cuando otra vista aplica coverage-apply
  useEffect(() => {
    const onCoverageChanged = () => load();
    window.addEventListener("coverage-changed", onCoverageChanged);
    return () => window.removeEventListener("coverage-changed", onCoverageChanged);
  }, []);

  const doImport = async (file, dryRun) => {
    if (!file) return;
    setImporting(true);
    const form = new FormData();
    form.append("file", file);
    try {
      const r = await axios.post(
        `${API}/delegado/admin/delegados/import-csv?dry_run=${dryRun}`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      setImportResult(r.data);
      if (!dryRun) {
        toast.success(`${r.data.applied_deleg} delegados · ${r.data.created_users} usuarios nuevos`);
        load();
        window.dispatchEvent(new Event("coverage-changed"));
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || t("common.error"));
    } finally { setImporting(false); }
  };

  const remove = async (email) => {
    if (!window.confirm(t("admin.delegados.form.deleteConfirm", { email }))) return;
    try {
      await axios.delete(`${API}/delegado/admin/delegados/${encodeURIComponent(email)}`);
      toast.success(t("admin.delegados.form.deleted"));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t("common.error"));
    }
  };

  return (
    <section className="mt-6 bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden" data-testid="delegados-panel">
      <header className="flex items-center justify-between gap-3 px-5 py-4 border-b border-gray-200 bg-gradient-to-r from-emerald-50 to-white">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
            <MapPin size={18} className="text-emerald-700"/>
          </div>
          <div>
            <h3 className="font-bold text-gray-900 font-['Outfit']">{t("admin.delegados.title")}</h3>
            <p className="text-xs text-gray-500">{t("admin.delegados.subtitle")}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowMap(v => !v)} className="text-xs bg-indigo-100 hover:bg-indigo-200 text-indigo-800 px-3 py-1.5 rounded flex items-center gap-1" data-testid="delegados-toggle-map">
            <MapIcon size={11}/> {showMap ? "Ocultar" : "Mostrar"} mapa
          </button>
          <button onClick={load} className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-1.5 rounded flex items-center gap-1" data-testid="delegados-reload">
            <RefreshCw size={11}/> {t("admin.delegados.reload")}
          </button>
          {/* iter 130: bulk import CSV */}
          <a
            href="data:text/csv;charset=utf-8,email,nombre,provincias,comunidades,ai_areas,score_min,high_value_floor,activo%0Ajm@valorizasm.com,Juan%20Manuel,Madrid|Toledo,Madrid|Castilla-La%20Mancha,residuos|recogida,75,5000000,true"
            download="delegados_ejemplo.csv"
            data-testid="delegados-csv-template"
            className="text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 px-3 py-1.5 rounded flex items-center gap-1"
            title="Descargar plantilla CSV">
            <Download size={11}/> Plantilla
          </a>
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            data-testid="delegados-csv-input"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) {
                setPendingFile(f);
                doImport(f, true);   // primero dry-run
              }
              e.target.value = "";
            }}/>
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={importing}
            data-testid="delegados-csv-import-btn"
            className="text-xs bg-indigo-600 hover:bg-indigo-700 text-white px-3 py-1.5 rounded flex items-center gap-1 disabled:opacity-50">
            {importing ? <Loader2 className="animate-spin" size={11}/> : <Upload size={11}/>} Importar CSV
          </button>
          <button onClick={() => setEditing(EMPTY_FORM)} className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1.5 rounded flex items-center gap-1" data-testid="delegados-new">
            <Plus size={11}/> {t("admin.delegados.newDelegado")}
          </button>
        </div>
      </header>

      {/* iter 130: modal dry-run del CSV import */}
      {importResult && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setImportResult(null)} data-testid="csv-import-result">
          <div className="bg-white rounded-2xl max-w-2xl w-full shadow-2xl max-h-[90vh] flex flex-col" onClick={e => e.stopPropagation()}>
            <div className={`px-5 py-3 rounded-t-2xl text-white ${importResult.dry_run ? "bg-slate-800" : "bg-emerald-600"}`}>
              <h3 className="font-bold text-sm">
                {importResult.dry_run ? "Preview CSV (dry-run)" : "CSV Importado"}
              </h3>
              <p className="text-[11px] opacity-90 mt-0.5">
                {importResult.total_rows} filas · {importResult.valid ?? importResult.applied_deleg} válidas · {importResult.errors?.length || 0} errores
              </p>
            </div>
            <div className="p-4 overflow-y-auto flex-1 text-xs space-y-3">
              {(importResult.preview || []).length > 0 && (
                <div>
                  <p className="font-bold text-slate-700 mb-1">Filas válidas (primeras 5):</p>
                  {importResult.preview.map((p, i) => (
                    <div key={i} className="border border-emerald-200 bg-emerald-50/50 rounded px-2 py-1 mb-1" data-testid={`csv-preview-${i}`}>
                      <b>{p.nombre}</b> · {p.email} · <span className="font-mono">{p.provincias.length} prov</span> · score {p.score_min}
                    </div>
                  ))}
                </div>
              )}
              {(importResult.errors || []).length > 0 && (
                <div>
                  <p className="font-bold text-rose-700 mb-1">Errores:</p>
                  {importResult.errors.map((e, i) => (
                    <div key={i} className="border border-rose-200 bg-rose-50/50 rounded px-2 py-1 mb-1 text-rose-800" data-testid={`csv-error-${i}`}>
                      fila {e.row}: {e.email || "(sin email)"} — <b>{e.error}</b>
                    </div>
                  ))}
                </div>
              )}
              {!importResult.dry_run && (
                <div className="border border-emerald-200 bg-emerald-50 rounded p-2 text-emerald-800">
                  ✓ {importResult.applied_deleg} delegados upserted · {importResult.created_users} usuarios nuevos creados con password temporal
                </div>
              )}
            </div>
            <div className="border-t border-slate-100 px-5 py-3 flex justify-end gap-2 bg-slate-50 rounded-b-2xl">
              <button
                onClick={() => setImportResult(null)}
                className="text-xs border border-slate-200 bg-white px-4 py-1.5 rounded text-slate-600 hover:bg-slate-100">
                Cerrar
              </button>
              {importResult.dry_run && importResult.valid > 0 && (
                <button
                  onClick={() => {
                    if (pendingFile) {
                      doImport(pendingFile, false);
                      setImportResult(null);
                      setPendingFile(null);
                    }
                  }}
                  data-testid="csv-import-apply"
                  className="text-xs bg-emerald-600 text-white px-4 py-1.5 rounded font-semibold hover:bg-emerald-700">
                  Aplicar los {importResult.valid} válidos
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {showMap && !loading && items.length > 0 && (
        <CoverageMap items={items} onEditDelegate={(d) => setEditing({ ...d })}/>
      )}

      {loading ? (
        <div className="p-10 text-center text-gray-500 text-sm flex items-center justify-center gap-2">
          <Loader2 className="animate-spin" size={14}/> {t("common.loading")}
        </div>
      ) : items.length === 0 ? (
        <div className="p-8 text-center text-gray-500 text-sm">
          {t("admin.delegados.empty")}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-gray-50 text-gray-600 text-[10px] uppercase tracking-wider">
              <tr>
                <th className="px-4 py-2 text-left">{t("admin.delegados.cols.delegado")}</th>
                <th className="px-4 py-2 text-left">{t("admin.delegados.cols.zone")}</th>
                <th className="px-4 py-2 text-left">{t("admin.delegados.cols.aiAreas")}</th>
                <th className="px-4 py-2 text-center">{t("admin.delegados.cols.scoreMin")}</th>
                <th className="px-4 py-2 text-center">HVF</th>
                <th className="px-4 py-2 text-center">{t("admin.delegados.cols.status")}</th>
                <th className="px-4 py-2 text-right">{t("admin.delegados.cols.actions")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {items.map(d => (
                <tr key={d.email} className="hover:bg-emerald-50/30" data-testid={`delegado-row-${d.email}`}>
                  <td className="px-4 py-3">
                    <p className="font-semibold text-gray-900">{d.nombre}</p>
                    <p className="text-[11px] text-gray-500 flex items-center gap-1"><Mail size={9}/> {d.email}</p>
                  </td>
                  <td className="px-4 py-3 text-gray-700">
                    {(d.provincias || []).slice(0, 3).join(", ")}
                    {(d.provincias || []).length > 3 && <span className="text-gray-400"> +{d.provincias.length - 3}</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-[11px]">
                    {(d.ai_areas || []).slice(0, 2).join(", ")}
                    {(d.ai_areas || []).length > 2 && <span className="text-gray-400"> +{d.ai_areas.length - 2}</span>}
                  </td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-700">{d.score_min || 75}</td>
                  <td className="px-4 py-3 text-center font-semibold text-sky-700 whitespace-nowrap" title="High Value Floor · umbral € por el cual una opp entra al scope aunque su AI score sea bajo">
                    {fmtEUR(d.high_value_floor || 200000)} €
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded ${d.activo ? "bg-emerald-100 text-emerald-700" : "bg-gray-200 text-gray-500"}`}>
                      {d.activo ? t("common.active") : t("common.inactive")}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setEditing({ ...d })}
                      className="text-emerald-600 hover:text-emerald-800 p-1.5 rounded hover:bg-emerald-100" data-testid={`delegado-edit-${d.email}`}>
                      <Edit2 size={13}/>
                    </button>
                    <button onClick={() => remove(d.email)}
                      className="text-rose-500 hover:text-rose-700 p-1.5 rounded hover:bg-rose-100 ml-1" data-testid={`delegado-delete-${d.email}`}>
                      <Trash2 size={13}/>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editing && <DelegadoFormModal initial={editing} onClose={(refresh) => { setEditing(null); if (refresh) load(); }}/>}
    </section>
  );
}


function DelegadoFormModal({ initial, onClose }) {
  const isEdit = !!initial.email && items_have(initial);
  const [form, setForm]     = useState({ ...EMPTY_FORM, ...initial });
  const [saving, setSaving] = useState(false);

  // Preview scope · dry-run
  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview]       = useState(null); // {total_count, hvf_ladder[], samples[]}

  // Provincias seleccionadas como Set para O(1) lookup
  const selectedProv = useMemo(() => new Set(form.provincias || []), [form.provincias]);
  const selectedCom  = useMemo(() => new Set(form.comunidades || []), [form.comunidades]);

  const toggleProv = (prov) => {
    setForm(f => {
      const set = new Set(f.provincias || []);
      if (set.has(prov)) set.delete(prov); else set.add(prov);
      return { ...f, provincias: [...set] };
    });
  };

  const toggleWholeCCAA = (ccaa) => {
    const provsInCCAA = CCAA_GROUPS[ccaa] || [];
    const aliases     = CCAA_ALIASES[ccaa] || [];
    const allSelected = provsInCCAA.every(p => selectedProv.has(p)) && aliases.some(a => selectedCom.has(a));
    setForm(f => {
      const provSet = new Set(f.provincias || []);
      const comSet  = new Set(f.comunidades || []);
      if (allSelected) {
        // Deseleccionar todas las provincias y quitar aliases
        provsInCCAA.forEach(p => provSet.delete(p));
        aliases.forEach(a => comSet.delete(a));
      } else {
        // Seleccionar todas las provincias y añadir el primer alias
        provsInCCAA.forEach(p => provSet.add(p));
        if (aliases[0]) comSet.add(aliases[0]);
      }
      return { ...f, provincias: [...provSet], comunidades: [...comSet] };
    });
  };

  const clearAll = () => setForm(f => ({ ...f, provincias: [], comunidades: [] }));

  const toggleArea = (a) => {
    setForm(f => ({
      ...f,
      ai_areas: (f.ai_areas || []).includes(a) ? f.ai_areas.filter(x => x !== a) : [...(f.ai_areas || []), a],
    }));
  };

  const save = async () => {
    if (!form.email || !form.nombre) {
      toast.error("Email y nombre son obligatorios"); return;
    }
    if (!form.provincias.length && !form.comunidades.length) {
      toast.error("Selecciona al menos una provincia o comunidad"); return;
    }
    setSaving(true);
    try {
      const r = await axios.put(`${API}/delegado/admin/delegados`, {
        email:            form.email.trim().toLowerCase(),
        nombre:           form.nombre.trim(),
        provincias:       form.provincias,
        comunidades:      form.comunidades,
        ai_areas:         form.ai_areas,
        score_min:        parseInt(form.score_min) || 75,
        high_value_floor: Number(form.high_value_floor) || 200000,
        activo:           form.activo,
      });
      if (r.data?.user_created) {
        toast.success(`✓ Delegado creado · cuenta de usuario también generada (pwd inicial: Valoriza2026!)`, { duration: 8000 });
      } else if (isEdit) {
        toast.success("Delegado actualizado");
      } else {
        toast.success("Delegado configurado (usuario ya existía)");
      }
      onClose(true);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error guardando");
    } finally { setSaving(false); }
  };

  const runPreview = async () => {
    if (!form.provincias.length && !form.comunidades.length) {
      toast.error("Selecciona zonas antes de previsualizar"); return;
    }
    setPreviewing(true);
    try {
      const currentFloor = Number(form.high_value_floor) || 200000;
      // Ladder centrado alrededor del valor actual
      const ladder = Array.from(new Set([50000, 100000, 200000, 500000, 1000000, 2000000, currentFloor]))
        .sort((a, b) => a - b);
      const r = await axios.post(`${API}/delegado/admin/delegados/preview-scope`, {
        provincias:       form.provincias,
        comunidades:      form.comunidades,
        ai_areas:         form.ai_areas,
        score_min:        parseInt(form.score_min) || 75,
        high_value_floor: currentFloor,
        sample_limit:     8,
        hvf_ladder:       ladder,
      });
      setPreview(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error calculando preview");
    } finally { setPreviewing(false); }
  };

  // Al cambiar zona/áreas/score/hvf, invalidar preview previo
  useEffect(() => { setPreview(null); }, [form.provincias, form.comunidades, form.ai_areas, form.score_min, form.high_value_floor]);

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4 overflow-y-auto" onClick={() => onClose(false)} data-testid="delegado-modal">
      <div className="bg-white rounded-xl max-w-4xl w-full p-5 my-8 shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-bold text-gray-900">{isEdit ? "Editar delegado" : "Nuevo delegado regional"}</h3>
          <button onClick={() => onClose(false)} className="text-gray-400 hover:text-gray-700"><X size={18}/></button>
        </div>

        <div className="space-y-4 text-xs">
          {/* Fila 1: identidad */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label>
              <span className="text-[10px] uppercase font-bold text-gray-600 tracking-wider">Email *</span>
              <input
                type="email"
                value={form.email}
                onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                disabled={isEdit}
                placeholder="delegado@valorizasm.com"
                data-testid="delegado-form-email"
                className="w-full border border-gray-200 rounded p-2 mt-1 focus:border-emerald-400 focus:outline-none disabled:bg-gray-50 disabled:text-gray-500"
              />
            </label>
            <label>
              <span className="text-[10px] uppercase font-bold text-gray-600 tracking-wider">Nombre completo *</span>
              <input
                type="text"
                value={form.nombre}
                onChange={e => setForm(f => ({ ...f, nombre: e.target.value }))}
                placeholder="Nombre Apellidos"
                data-testid="delegado-form-nombre"
                className="w-full border border-gray-200 rounded p-2 mt-1 focus:border-emerald-400 focus:outline-none"
              />
            </label>
          </div>

          {/* Provincias por CCAA · checkboxes */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-[10px] uppercase font-bold text-gray-600 tracking-wider">Zonas cubiertas · marca las provincias del delegado</span>
              <div className="flex items-center gap-2">
                <span className="text-[10px] text-gray-500">{form.provincias.length} prov · {form.comunidades.length} CCAA aliases</span>
                <button type="button" onClick={clearAll} className="text-[10px] text-rose-600 hover:underline" data-testid="delegado-form-clearall">Limpiar todo</button>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 max-h-72 overflow-y-auto border border-gray-100 rounded p-2 bg-gray-50/40" data-testid="delegado-form-provincias-grid">
              {Object.entries(CCAA_GROUPS).map(([ccaa, provs]) => {
                const allSel = provs.every(p => selectedProv.has(p));
                const someSel = provs.some(p => selectedProv.has(p));
                return (
                  <div key={ccaa} className="bg-white border border-gray-100 rounded p-2">
                    <label className="flex items-center gap-1.5 cursor-pointer" data-testid={`delegado-ccaa-${ccaa}`}>
                      <input
                        type="checkbox"
                        checked={allSel}
                        ref={el => { if (el) el.indeterminate = !allSel && someSel; }}
                        onChange={() => toggleWholeCCAA(ccaa)}
                        className="w-3.5 h-3.5 accent-emerald-600"
                      />
                      <span className="text-[11px] font-bold text-gray-800">{ccaa}</span>
                    </label>
                    <div className="mt-1.5 pl-4 space-y-1">
                      {provs.map(p => (
                        <label key={p} className="flex items-center gap-1.5 cursor-pointer" data-testid={`delegado-prov-${p}`}>
                          <input
                            type="checkbox"
                            checked={selectedProv.has(p)}
                            onChange={() => toggleProv(p)}
                            className="w-3 h-3 accent-emerald-600"
                          />
                          <span className={`text-[11px] ${selectedProv.has(p) ? "text-gray-900 font-medium" : "text-gray-600"}`}>{p}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-[10px] text-gray-400 mt-1">
              💡 Marcar la CCAA añade también sus alias como <code>comunidades</code> (regex vía título/organism). Deselecciona la CCAA si quieres solo provincias específicas (ej. Andalucía Este: Granada+Málaga+Almería+Jaén sin toda la CCAA).
            </p>
          </div>

          {/* Áreas Pando */}
          <div>
            <span className="text-[10px] uppercase font-bold text-gray-600 tracking-wider">Áreas Pando · Core Valoriza</span>
            <div className="flex flex-wrap gap-1.5 mt-1.5" data-testid="delegado-form-areas">
              {DEFAULT_AI_AREAS.map(a => (
                <button
                  key={a}
                  type="button"
                  onClick={() => toggleArea(a)}
                  className={`text-[10px] px-2 py-1 rounded border ${(form.ai_areas || []).includes(a) ? "bg-emerald-600 text-white border-emerald-600" : "bg-white text-gray-500 border-gray-200 hover:border-emerald-300"}`}>
                  {a.replace(/_/g, " ")}
                </button>
              ))}
            </div>
          </div>

          {/* Umbrales: slider + input high_value_floor */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-gradient-to-br from-emerald-50 to-white border border-emerald-100 rounded p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] uppercase font-bold text-emerald-700 tracking-wider flex items-center gap-1">
                  <Sliders size={11}/> Score Pando mínimo
                </span>
                <span className="text-lg font-black text-emerald-700 tabular-nums" data-testid="delegado-form-score-value">{form.score_min}</span>
              </div>
              <input
                type="range"
                min="0" max="100" step="5"
                value={form.score_min}
                onChange={e => setForm(f => ({ ...f, score_min: parseInt(e.target.value) }))}
                data-testid="delegado-form-score-slider"
                className="w-full accent-emerald-600"
              />
              <div className="flex justify-between text-[9px] text-gray-400 mt-0.5">
                <span>0 · todo</span><span>75 · recomendado</span><span>100 · muy estricto</span>
              </div>
            </div>

            <div className="bg-gradient-to-br from-sky-50 to-white border border-sky-100 rounded p-3">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[10px] uppercase font-bold text-sky-700 tracking-wider flex items-center gap-1">
                  <Euro size={11}/> High Value Floor
                </span>
                <span className="text-[10px] text-gray-500">€ / puerta secundaria</span>
              </div>
              <input
                type="number"
                min="0" step="10000"
                value={form.high_value_floor}
                onChange={e => setForm(f => ({ ...f, high_value_floor: e.target.value }))}
                data-testid="delegado-form-hvf"
                className="w-full border border-gray-200 rounded p-2 mt-1 focus:border-sky-400 focus:outline-none text-sm font-semibold"
              />
              <p className="text-[10px] text-gray-500 mt-1">
                Opps con <code>value_eur ≥</code> este importe entran al digest <b>aunque el AI score sea bajo</b>. Default: 200.000 €.
              </p>
            </div>
          </div>

          {/* Botón preview scope · dry-run */}
          <div className="pt-1">
            <button
              type="button"
              onClick={runPreview}
              disabled={previewing || (!form.provincias.length && !form.comunidades.length)}
              data-testid="delegado-form-preview-btn"
              className="w-full text-xs bg-gradient-to-r from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700 text-white px-4 py-2 rounded flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              {previewing ? <Loader2 size={13} className="animate-spin"/> : <Eye size={13}/>}
              {previewing ? "Calculando…" : "Vista previa del scope · dry-run"}
            </button>
            <p className="text-[10px] text-gray-400 mt-1 text-center">Aplica los valores del formulario a la base y muestra cuántas opps caerían — sin guardar.</p>
          </div>

          {preview && <ScopePreviewPanel preview={preview} currentFloor={Number(form.high_value_floor) || 200000} onPickFloor={(v) => setForm(f => ({ ...f, high_value_floor: v }))}/>}

          {/* Activo */}
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={form.activo}
              onChange={e => setForm(f => ({ ...f, activo: e.target.checked }))}
              data-testid="delegado-form-activo"
              className="w-4 h-4 accent-emerald-600"
            />
            <span className="text-xs text-gray-700">Activo (recibe emails del digest diario y semanal)</span>
          </label>

          {!isEdit && (
            <div className="mt-3 p-3 bg-emerald-50 border-l-2 border-emerald-400 rounded text-[11px] text-emerald-900">
              <b>✓ Creación automática:</b> Al guardar, si el usuario no existe se creará automáticamente con rol
              <code className="bg-emerald-100 px-1 rounded mx-1">delegado_regional</code>, contraseña inicial
              <code className="bg-emerald-100 px-1 rounded mx-1">Valoriza2026!</code> y cambio obligatorio al primer login.
              El delegado podrá entrar directamente en <code className="bg-emerald-100 px-1 rounded">/delegado</code>.
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 mt-5 pt-4 border-t border-gray-200">
          <button onClick={() => onClose(false)} className="text-xs border border-gray-200 px-3 py-1.5 rounded text-gray-600 hover:bg-gray-50" data-testid="delegado-form-cancel">Cancelar</button>
          <button onClick={save} disabled={saving} className="text-xs bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-1.5 rounded flex items-center gap-1 disabled:opacity-50" data-testid="delegado-form-save">
            {saving ? <Loader2 size={11} className="animate-spin"/> : <Save size={11}/>}
            {isEdit ? "Guardar cambios" : "Crear delegado"}
          </button>
        </div>
      </div>
    </div>
  );
}

// Detecta si el initial viene de un delegado ya persistido (isEdit) vs form nuevo
function items_have(initial) {
  return Boolean(initial && (initial.provincias?.length || initial.comunidades?.length || initial.nombre));
}


// ═══════════════════════════ Coverage map ═══════════════════════════
//
// Aggregate coverage per province from the delegates list and render a
// tile grid grouped by CCAA. Green = covered by ≥1 active delegate,
// amber = only covered by an inactive delegate, red = no delegate at all.
// Click a tile to see who covers it and jump to edit.

function normalize(s) {
  return (s || "").toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
}

function computeCoverage(items) {
  // Map: province → [{delegate, activo}]
  const coverage = {};
  Object.values(CCAA_GROUPS).flat().forEach(p => { coverage[p] = []; });

  items.forEach(d => {
    const provSet = new Set((d.provincias || []).map(normalize));
    const comSet  = new Set((d.comunidades || []).map(normalize));
    Object.entries(CCAA_GROUPS).forEach(([ccaa, provs]) => {
      const ccaaAliases = (CCAA_ALIASES[ccaa] || []).map(normalize);
      const covers_whole_ccaa = ccaaAliases.some(a => comSet.has(a));
      provs.forEach(p => {
        if (provSet.has(normalize(p)) || covers_whole_ccaa) {
          coverage[p].push({ email: d.email, nombre: d.nombre, activo: !!d.activo });
        }
      });
    });
  });

  return coverage;
}

function CoverageMap({ items, onEditDelegate }) {
  const [selected, setSelected] = useState(null); // { province, delegates[] }

  const coverage = useMemo(() => computeCoverage(items), [items]);
  const stats = useMemo(() => {
    let covered = 0, inactiveOnly = 0, uncovered = 0;
    Object.values(coverage).forEach(list => {
      const active = list.filter(x => x.activo).length;
      if (active > 0) covered++;
      else if (list.length > 0) inactiveOnly++;
      else uncovered++;
    });
    const total = covered + inactiveOnly + uncovered;
    return { covered, inactiveOnly, uncovered, total };
  }, [coverage]);

  const tileClass = (list) => {
    const active = list.filter(x => x.activo).length;
    if (active > 0)      return "bg-emerald-500 hover:bg-emerald-600 text-white border-emerald-600";
    if (list.length > 0) return "bg-amber-400 hover:bg-amber-500 text-amber-950 border-amber-500";
    return "bg-rose-100 hover:bg-rose-200 text-rose-800 border-rose-300";
  };

  return (
    <div className="border-b border-gray-200 bg-gradient-to-b from-slate-50 to-white p-5" data-testid="delegados-coverage-map">
      {/* Legend + stats */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] uppercase font-bold text-gray-500 tracking-wider mr-1">Mapa de cobertura</span>
          <span className="inline-flex items-center gap-1 text-[11px] text-gray-700"><span className="w-3 h-3 rounded-sm bg-emerald-500 border border-emerald-600"/> Con delegado activo</span>
          <span className="inline-flex items-center gap-1 text-[11px] text-gray-700"><span className="w-3 h-3 rounded-sm bg-amber-400 border border-amber-500"/> Solo delegado inactivo</span>
          <span className="inline-flex items-center gap-1 text-[11px] text-gray-700"><span className="w-3 h-3 rounded-sm bg-rose-100 border border-rose-300"/> Sin delegado</span>
        </div>
        <div className="flex items-center gap-3 text-[11px]">
          <span className="font-mono tabular-nums">
            <b className="text-emerald-700">{stats.covered}</b>/<span className="text-gray-500">{stats.total}</span>
            <span className="text-gray-500 ml-1">cubiertas</span>
          </span>
          {stats.uncovered > 0 && (
            <span className="font-mono tabular-nums text-rose-700 flex items-center gap-1">
              <AlertCircle size={11}/><b>{stats.uncovered}</b> huecos
            </span>
          )}
        </div>
      </div>

      {/* Grid CCAA */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3" data-testid="coverage-grid">
        {Object.entries(CCAA_GROUPS).map(([ccaa, provs]) => {
          const activeCount = provs.filter(p => coverage[p].some(x => x.activo)).length;
          const anyCount    = provs.filter(p => coverage[p].length > 0).length;
          const ccaaColor = activeCount === provs.length ? "border-emerald-300 bg-emerald-50/40"
                         : activeCount > 0                ? "border-amber-200 bg-white"
                         : anyCount > 0                   ? "border-amber-200 bg-amber-50/30"
                                                          : "border-rose-200 bg-rose-50/30";
          return (
            <div key={ccaa} className={`border rounded-lg p-2 ${ccaaColor}`}>
              <div className="flex items-center justify-between mb-1.5 px-0.5">
                <span className="text-[11px] font-bold text-gray-800">{ccaa}</span>
                <span className="text-[10px] tabular-nums text-gray-500">{activeCount}/{provs.length}</span>
              </div>
              <div className="flex flex-wrap gap-1">
                {provs.map(p => {
                  const list = coverage[p];
                  return (
                    <button
                      key={p}
                      type="button"
                      onClick={() => setSelected({ province: p, delegates: list })}
                      className={`text-[10px] px-1.5 py-1 rounded border transition ${tileClass(list)}`}
                      title={list.length ? list.map(x => `${x.nombre}${x.activo ? "" : " (inactivo)"}`).join(", ") : "Sin delegado"}
                      data-testid={`coverage-tile-${p}`}
                    >
                      {p}
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Popover for the selected province */}
      {selected && (
        <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" onClick={() => setSelected(null)} data-testid="coverage-popover">
          <div className="bg-white rounded-xl max-w-md w-full p-5 shadow-2xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <p className="text-[10px] uppercase font-bold text-gray-500 tracking-wider">Provincia</p>
                <h4 className="text-lg font-bold text-gray-900">{selected.province}</h4>
              </div>
              <button onClick={() => setSelected(null)} className="text-gray-400 hover:text-gray-700"><X size={18}/></button>
            </div>

            {selected.delegates.length === 0 ? (
              <div className="bg-rose-50 border border-rose-200 rounded p-3 text-[12px] text-rose-800">
                <b>⚠️ Sin delegado asignado.</b><br/>
                Ningún delegado tiene esta provincia en su zona ni cubre la CCAA. Considera dar de alta un nuevo delegado o ampliar la zona de uno existente.
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-[11px] text-gray-500">Delegados que cubren esta provincia:</p>
                {selected.delegates.map(d => (
                  <button
                    key={d.email}
                    type="button"
                    onClick={() => {
                      const full = items.find(x => x.email === d.email);
                      if (full) { setSelected(null); onEditDelegate(full); }
                    }}
                    className={`w-full flex items-center gap-2 border rounded p-2 hover:shadow transition text-left ${d.activo ? "border-emerald-200 hover:border-emerald-400 bg-emerald-50/40" : "border-gray-200 hover:border-gray-400 bg-gray-50 opacity-75"}`}
                    data-testid={`coverage-popover-delegate-${d.email}`}
                  >
                    <div className={`w-2 h-2 rounded-full ${d.activo ? "bg-emerald-500" : "bg-gray-400"}`}/>
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-gray-900 text-xs truncate">{d.nombre}</p>
                      <p className="text-[10px] text-gray-500 truncate">{d.email}</p>
                    </div>
                    <span className={`text-[9px] uppercase font-bold px-1.5 py-0.5 rounded ${d.activo ? "bg-emerald-100 text-emerald-700" : "bg-gray-200 text-gray-500"}`}>
                      {d.activo ? "activo" : "inactivo"}
                    </span>
                    <Edit2 size={11} className="text-gray-400"/>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}



function ScopePreviewPanel({ preview, currentFloor, onPickFloor }) {
  const { total_count, hvf_ladder = [], samples = [] } = preview || {};
  const maxCount = Math.max(1, ...hvf_ladder.map(l => l.count));

  return (
    <div className="mt-2 border border-indigo-200 rounded-lg overflow-hidden shadow-sm" data-testid="delegado-scope-preview">
      <div className="bg-gradient-to-r from-indigo-50 to-violet-50 px-4 py-2.5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TrendingUp size={14} className="text-indigo-700"/>
          <span className="text-[11px] uppercase font-bold text-indigo-800 tracking-wider">Vista previa del scope</span>
        </div>
        <div className="text-right">
          <div className="text-2xl font-black text-indigo-700 tabular-nums leading-none" data-testid="scope-preview-total">{total_count}</div>
          <div className="text-[10px] text-gray-500 -mt-0.5">opps en digest</div>
        </div>
      </div>

      <div className="p-3 space-y-3 bg-white">
        {/* HVF ladder */}
        {hvf_ladder.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] uppercase font-bold text-gray-600 tracking-wider">Impacto de <code>high_value_floor</code></span>
              <span className="text-[10px] text-gray-400">clic para aplicar</span>
            </div>
            <div className="space-y-1">
              {hvf_ladder.map((row) => {
                const w = (row.count / maxCount) * 100;
                const active = Math.abs(row.floor - currentFloor) < 1;
                return (
                  <button
                    key={row.floor}
                    type="button"
                    onClick={() => onPickFloor(row.floor)}
                    data-testid={`scope-preview-ladder-${row.floor}`}
                    className={`w-full flex items-center gap-2 group hover:bg-indigo-50 rounded px-1.5 py-0.5 transition ${active ? "bg-indigo-100/60" : ""}`}
                  >
                    <span className={`text-[11px] tabular-nums font-mono ${active ? "text-indigo-700 font-bold" : "text-gray-600"} w-24 text-right`}>
                      {fmtEUR(row.floor)} €
                    </span>
                    <div className="flex-1 h-4 bg-gray-100 rounded overflow-hidden relative">
                      <div className={`h-full ${active ? "bg-indigo-600" : "bg-indigo-400 group-hover:bg-indigo-500"} transition-all`} style={{ width: `${w}%` }} />
                    </div>
                    <span className={`text-[11px] tabular-nums w-10 text-right ${active ? "text-indigo-700 font-bold" : "text-gray-700"}`}>{row.count}</span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Samples */}
        {samples.length > 0 && (
          <div>
            <div className="text-[10px] uppercase font-bold text-gray-600 tracking-wider mb-1.5">Top {samples.length} ejemplos</div>
            <div className="space-y-1 max-h-40 overflow-y-auto" data-testid="scope-preview-samples">
              {samples.map((s, i) => (
                <div key={s.external_id || i} className="text-[11px] flex items-start gap-2 p-1.5 border border-gray-100 rounded hover:bg-gray-50">
                  <div className="flex flex-col items-center min-w-[42px]">
                    <span className={`text-[10px] font-black tabular-nums ${(s.ai_score || 0) >= 75 ? "text-emerald-700" : (s.ai_score || 0) >= 50 ? "text-amber-600" : "text-rose-500"}`}>{s.ai_score ?? "–"}</span>
                    <span className="text-[8px] text-gray-400 uppercase">score</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-gray-900 font-medium truncate">{s.title || "—"}</p>
                    <p className="text-[10px] text-gray-500 truncate">{s.organism || "—"} · <span className="text-sky-700 font-semibold">{fmtEUR(s.value_eur)} €</span> · <span className="text-gray-400">{s.ai_area}</span></p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {total_count === 0 && (
          <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
            ⚠️ Con estos parámetros, el delegado NO recibiría ninguna opp. Considera bajar el <code>score_min</code>, ampliar áreas Pando o reducir el <code>high_value_floor</code>.
          </div>
        )}
      </div>
    </div>
  );
}
